import {
  AllCandidateBLASTUiParamsType,
  CommonBLASTParamValues,
  MultiChoiceBLASTParameter,
  UnwrappedBLASTParamsType,
} from 'search/blast/parameters/common';
import {BLASTGapCost, BLASTMatchScore, BLASTNTask} from 'search/SearchConstants';
import pickDefined from 'util/pickDefined';

export type CandidateBLASTNUiParamsType = Pick<
  AllCandidateBLASTUiParamsType<BLASTNTask>,
  'task' | 'evalue' | 'word_size' | 'culling_limit' | 'gap_cost' | 'match_score' | 'strand'
>;

export type BLASTNUiParamsType = UnwrappedBLASTParamsType<CandidateBLASTNUiParamsType>;

class MatchScoreToGapCostMap extends Map<BLASTMatchScore, MultiChoiceBLASTParameter<BLASTGapCost>> {
  private store: Map<string, MultiChoiceBLASTParameter<BLASTGapCost>> = new Map();

  public override set(matchScore: BLASTMatchScore, gapCosts: MultiChoiceBLASTParameter<BLASTGapCost>): this {
    this.store.set(this.getKey(matchScore), gapCosts);
    return this;
  }

  public override has(matchScore: BLASTMatchScore): boolean {
    return this.store.has(this.getKey(matchScore));
  }

  public override get(matchScore: BLASTMatchScore): MultiChoiceBLASTParameter<BLASTGapCost> | undefined {
    return this.store.get(this.getKey(matchScore));
  }

  public getWithConfidence(matchScore: BLASTMatchScore): MultiChoiceBLASTParameter<BLASTGapCost> {
    const value = this.store.get(this.getKey(matchScore));
    if (value === undefined) {
      throw new Error(`Match score does not exists: ${JSON.stringify(matchScore || {})}`);
    }
    return value;
  }

  public getAllKeys(): Array<BLASTMatchScore> {
    return Array.from(this.store.keys()).map((keyString) => {
      const [rewardString, penaltyString] = keyString.split(',');
      return {
        reward: parseInt(rewardString, 10),
        penalty: parseInt(penaltyString, 10),
      } as BLASTMatchScore;
    });
  }

  private getKey(matchScore: BLASTMatchScore): string {
    return `${matchScore.reward},${matchScore.penalty}`;
  }
}

export const MegaBlastNMatchScoreToGapCosts = new MatchScoreToGapCostMap()
  .set(
    {reward: 1, penalty: -2},
    {
      values: [
        {existence: 0, extension: undefined},
        {existence: 5, extension: 2},
        {existence: 2, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 3, extension: 1},
        {existence: 2, extension: 1},
        {existence: 1, extension: 1},
      ],
      defaultValue: {existence: 0, extension: undefined},
    }
  )
  .set(
    {reward: 1, penalty: -3},
    {
      values: [
        {existence: 0, extension: undefined},
        {existence: 5, extension: 2},
        {existence: 2, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 2, extension: 1},
        {existence: 1, extension: 1},
      ],
      defaultValue: {existence: 0, extension: undefined},
    }
  )
  .set(
    {reward: 1, penalty: -4},
    {
      values: [
        {existence: 0, extension: undefined},
        {existence: 5, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 2, extension: 1},
        {existence: 1, extension: 1},
      ],
      defaultValue: {existence: 0, extension: undefined},
    }
  )
  .set(
    {reward: 2, penalty: -3},
    {
      values: [
        {existence: 0, extension: undefined},
        {existence: 4, extension: 4},
        {existence: 2, extension: 4},
        {existence: 0, extension: 4},
        {existence: 3, extension: 3},
        {existence: 6, extension: 2},
        {existence: 5, extension: 2},
        {existence: 4, extension: 2},
        {existence: 2, extension: 2},
      ],
      defaultValue: {existence: 0, extension: undefined},
    }
  )
  .set(
    {reward: 4, penalty: -5},
    {
      values: [
        {existence: 0, extension: undefined},
        {existence: 12, extension: 8},
        {existence: 6, extension: 5},
        {existence: 5, extension: 5},
        {existence: 4, extension: 5},
        {existence: 3, extension: 5},
      ],
      defaultValue: {existence: 0, extension: undefined},
    }
  )
  .set(
    {reward: 1, penalty: -1},
    {
      values: [
        {existence: 5, extension: 2},
        {existence: 3, extension: 2},
        {existence: 2, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 4, extension: 1},
        {existence: 3, extension: 1},
        {existence: 2, extension: 1},
      ],
      defaultValue: {existence: 5, extension: 2},
    }
  );

export const BlastNMatchScoreToGapCosts = new MatchScoreToGapCostMap()
  .set(
    {reward: 1, penalty: -2},
    {
      values: [
        {existence: 5, extension: 2},
        {existence: 2, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 3, extension: 1},
        {existence: 2, extension: 1},
        {existence: 1, extension: 1},
      ],
      defaultValue: {existence: 5, extension: 2},
    }
  )
  .set(
    {reward: 1, penalty: -3},
    {
      values: [
        {existence: 5, extension: 2},
        {existence: 2, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 2, extension: 1},
        {existence: 1, extension: 1},
      ],
      defaultValue: {existence: 5, extension: 2},
    }
  )
  .set(
    {reward: 1, penalty: -4},
    {
      values: [
        {existence: 5, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 2, extension: 1},
        {existence: 1, extension: 1},
      ],
      defaultValue: {existence: 5, extension: 2},
    }
  )
  .set(
    {reward: 2, penalty: -3},
    {
      values: [
        {existence: 4, extension: 4},
        {existence: 2, extension: 4},
        {existence: 0, extension: 4},
        {existence: 3, extension: 3},
        {existence: 6, extension: 2},
        {existence: 5, extension: 2},
        {existence: 4, extension: 2},
        {existence: 2, extension: 2},
      ],
      defaultValue: {existence: 5, extension: 2},
    }
  )
  .set(
    {reward: 4, penalty: -5},
    {
      values: [
        {existence: 12, extension: 8},
        {existence: 6, extension: 5},
        {existence: 5, extension: 5},
        {existence: 4, extension: 5},
        {existence: 3, extension: 5},
      ],
      defaultValue: {existence: 12, extension: 8},
    }
  )
  .set(
    {reward: 1, penalty: -1},
    {
      values: [
        {existence: 5, extension: 2},
        {existence: 3, extension: 2},
        {existence: 2, extension: 2},
        {existence: 1, extension: 2},
        {existence: 0, extension: 2},
        {existence: 4, extension: 1},
        {existence: 3, extension: 1},
        {existence: 2, extension: 1},
      ],
      defaultValue: {existence: 5, extension: 2},
    }
  );

const CommonBLASTNParams: Pick<CandidateBLASTNUiParamsType, 'culling_limit' | 'strand'> = {
  ...pickDefined(CommonBLASTParamValues, 'culling_limit', 'strand'),
};

export const DefaultMegaBLASTNParams: CandidateBLASTNUiParamsType = {
  ...CommonBLASTNParams,
  task: {defaultValue: BLASTNTask.MegaBLAST, values: Object.values(BLASTNTask)},
  evalue: {defaultValue: 10},
  word_size: {
    values: [16, 20, 24, 28, 32, 48, 64, 128, 256],
    defaultValue: 28,
  },
  match_score: {
    values: MegaBlastNMatchScoreToGapCosts.getAllKeys(),
    defaultValue: {reward: 1, penalty: -2},
  },
  gap_cost: MegaBlastNMatchScoreToGapCosts.getWithConfidence({reward: 1, penalty: -2}),
};

export const DefaultDcMegaBLASTNParams: CandidateBLASTNUiParamsType = {
  ...CommonBLASTNParams,
  task: {defaultValue: BLASTNTask.DCMegaBLAST, values: Object.values(BLASTNTask)},
  evalue: {defaultValue: 10},
  word_size: {
    values: [11, 12],
    defaultValue: 11,
  },
  match_score: {
    values: BlastNMatchScoreToGapCosts.getAllKeys(),
    defaultValue: {reward: 2, penalty: -3},
  },
  gap_cost: BlastNMatchScoreToGapCosts.getWithConfidence({reward: 2, penalty: -3}),
};

export const DefaultBLASTNParams: CandidateBLASTNUiParamsType = {
  ...CommonBLASTNParams,
  task: {defaultValue: BLASTNTask.BLASTN, values: Object.values(BLASTNTask)},
  evalue: {defaultValue: 10},
  word_size: {
    values: [7, 11, 15],
    defaultValue: 11,
  },
  match_score: {
    values: BlastNMatchScoreToGapCosts.getAllKeys(),
    defaultValue: {reward: 2, penalty: -3},
  },
  gap_cost: BlastNMatchScoreToGapCosts.getWithConfidence({reward: 2, penalty: -3}),
};

export const DefaultBLASTNShortParams: CandidateBLASTNUiParamsType = {
  ...CommonBLASTNParams,
  task: {defaultValue: BLASTNTask.BLASTNShort, values: Object.values(BLASTNTask)},
  evalue: {defaultValue: 1000},
  word_size: {
    values: [7, 11, 15],
    defaultValue: 7,
  },
  match_score: {
    values: BlastNMatchScoreToGapCosts.getAllKeys(),
    defaultValue: {reward: 1, penalty: -3},
  },
  gap_cost: BlastNMatchScoreToGapCosts.getWithConfidence({reward: 1, penalty: -3}),
};

export const getCandidateBLASTNUiParams = (
  task?: BLASTNTask,
  matchScore?: BLASTMatchScore
): CandidateBLASTNUiParamsType => {
  if (matchScore === undefined) {
    switch (task) {
      case BLASTNTask.DCMegaBLAST:
        return DefaultDcMegaBLASTNParams;
      case BLASTNTask.BLASTN:
        return DefaultBLASTNParams;
      case BLASTNTask.BLASTNShort:
        return DefaultBLASTNShortParams;
      default:
        return DefaultMegaBLASTNParams;
    }
  }

  const defaultParams = {...getCandidateBLASTNUiParams(task)};
  switch (task) {
    case BLASTNTask.DCMegaBLAST:
    case BLASTNTask.BLASTN:
    case BLASTNTask.BLASTNShort: {
      defaultParams.match_score = {
        ...defaultParams.match_score,
        defaultValue: {...matchScore},
      };
      const defaultGapCosts = BlastNMatchScoreToGapCosts.getWithConfidence(matchScore);
      defaultParams.gap_cost = {...defaultGapCosts};
      return defaultParams;
    }
    default: {
      defaultParams.match_score = {
        ...defaultParams.match_score,
        defaultValue: {...matchScore},
      };
      const defaultGapCosts = MegaBlastNMatchScoreToGapCosts.getWithConfidence(matchScore);
      defaultParams.gap_cost = {...defaultGapCosts};
      return defaultParams;
    }
  }
};
